import { useState, useEffect, useCallback } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import {
  startGetAdsCampaign,
  startGetAdsCampaigns,
  startUpdateAdsCampaignsStatusAndStatisticsFromPlatforms,
  startGetHomeMetricsConfiguration,
  startSetHomeMetricsConfiguration,
  removeAdsCampaignStatusOverrides,
} from "../actions/adsCampaigns";
import { getPathDatabaseByRole } from "../actions/auth";
import { getStatusAdsCampaign } from "../services/adsCampaigns";
import {
  ADS_CAMPAIGN_STATUS_DRAFT,
  ALERTS_ASYNC_STATUS,
  DEFAULT_SORT_CONFIG,
  SORTABLE_COLUMNS,
  SORT_DIRECTION,
  DATE_RANGE,
} from "../utils/constants";

/**
 * Custom hook to manage ads campaigns list, sorting and pagination
 * @returns {Object} campaigns state, sort config, handlers
 */
const useAdsCampaignsList = () => {
  const dispatch = useDispatchApp();
  const { adsCampaigns, statusOverrides = {} } = useSelectorApp(
    (state) => state.adsCampaigns
  );
  const { alerts = [] } = useSelectorApp((state) => state.alerts);

  const publishingCampaigns = alerts.filter(
    (alert) => alert.type === "campaign"
  );

  const { userID } = dispatch(getPathDatabaseByRole());

  // State management
  const [campaigns, setCampaigns] = useState(null);
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [lastCampaignID, setLastCampaignID] = useState(null);
  const [loadedLastCampaign, setLoadedLastCampaign] = useState(false);
  const [campaignsSynced, setCampaignsSynced] = useState([]);
  const [syncingCampaignIDs, setSyncingCampaignIDs] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [subValueSort, setSubValueSort] = useState(null);
  const [datePresetKey, setDatePresetKey] = useState(null);
  const [savedDateRange, setSavedDateRange] = useState(null);

  // Update campaigns information from platforms
  const onUpdateAdsCampaignInformationFromPlatforms = useCallback(
    async (campaignIDs) => {
      const response = await dispatch(
        startUpdateAdsCampaignsStatusAndStatisticsFromPlatforms({
          campaignIDs,
        })
      );
      return response;
    },
    [dispatch]
  );

  // Load initial data
  const getInitialData = useCallback(async () => {
    if (isLoadingConfiguration) return;
    setCampaigns(null);
    setIsLoading(true);
    setLastCampaignID(null);
    setLoadedLastCampaign(false);
    await dispatch(
      startGetAdsCampaigns({
        onChangeLastKey: (lastKey) => setLastCampaignID(lastKey),
        onLoadLastKey: () => setLoadedLastCampaign(true),
        sortConfig,
        subValueSort,
        onChangeSubValueSort: (subValueSort) => setSubValueSort(subValueSort),
      })
    );
    setIsLoading(false);
  }, [dispatch, sortConfig, subValueSort, isLoadingConfiguration]);

  // Load next batch of campaigns (pagination)
  const onGetNextCampaigns = useCallback(async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    await dispatch(
      startGetAdsCampaigns({
        lastKey: lastCampaignID,
        onChangeLastKey: (lastKey) => setLastCampaignID(lastKey),
        onLoadLastKey: () => setLoadedLastCampaign(true),
        sortConfig,
        subValueSort,
        onChangeSubValueSort: (subValueSort) => setSubValueSort(subValueSort),
      })
    );
    setIsSyncing(false);
  }, [dispatch, lastCampaignID, isSyncing, sortConfig, subValueSort]);

  // Load saved sort configuration from DB
  useEffect(() => {
    const loadSavedSortConfig = async () => {
      const response = await dispatch(startGetHomeMetricsConfiguration());
      if (!response?.ok) {
        setIsLoadingConfiguration(false);
        return;
      }

      const config = response?.data || {};

      const preset = config?.tablePreset;
      if (!preset) {
        setIsLoadingConfiguration(false);
      } else {
        const savedKey = preset?.sortableColumn;
        const savedDirection =
          preset?.sortDirection === SORT_DIRECTION.DESC
            ? SORT_DIRECTION.DESC
            : SORT_DIRECTION.ASC;

        const isValidKey = Object.values(SORTABLE_COLUMNS).includes(savedKey);
        if (isValidKey) {
          setSortConfig({ key: savedKey, direction: savedDirection });
        }
      }

      const savedFilter = config.filterCampaingsByDates;
      const dateRangeValues = Object.values(DATE_RANGE);
      const isValidFilter = dateRangeValues.includes(savedFilter);

      if (!savedFilter || savedFilter === "none" || !isValidFilter) {
        setDatePresetKey(DATE_RANGE.last_14);
        setSavedDateRange(null);
      } else {
        setDatePresetKey(savedFilter);

        if (savedFilter === DATE_RANGE.custom) {
          const { startDate, endDate } = config?.customDates || {};
          const hasValidCustomDates =
            typeof startDate === "number" && typeof endDate === "number";

          setSavedDateRange(
            hasValidCustomDates
              ? {
                  startDateTime: new Date(startDate),
                  endDateTime: new Date(endDate),
                }
              : null
          );
        } else {
          setSavedDateRange(null);
        }
      }

      setIsLoadingConfiguration(false);
    };

    loadSavedSortConfig();
    // eslint-disable-next-line
  }, [userID]);

  // Handle table sorting
  const onTableSort = useCallback(
    (columnKey, columnVisibility = {}) => {
      setSortConfig((prevConfig) => {
        const newDirection =
          prevConfig.key === columnKey &&
          prevConfig.direction === SORT_DIRECTION.DESC
            ? SORT_DIRECTION.ASC
            : SORT_DIRECTION.DESC;

        dispatch(
          startSetHomeMetricsConfiguration({
            metricsConfiguration: {
              ...columnVisibility,
              tablePreset: {
                sortableColumn: columnKey,
                sortDirection:
                  newDirection === SORT_DIRECTION.ASC ? "asc" : "desc",
              },
            },
          })
        );

        return { key: columnKey, direction: newDirection };
      });

      setSubValueSort(null);
    },
    [dispatch]
  );

  const onChangeDatePresetKey = useCallback(
    ({ presetKey: nextKey, startDate = null, endDate = null }) => {
      const dateRangeValues = Object.values(DATE_RANGE);
      const isValidFilter = dateRangeValues.includes(nextKey);

      const normalizedKey = !nextKey
        ? DATE_RANGE.last_14
        : isValidFilter
          ? nextKey
          : DATE_RANGE.last_14;

      setDatePresetKey(normalizedKey);

      const hasValidCustomDates =
        normalizedKey === DATE_RANGE.custom &&
        typeof startDate === "number" &&
        typeof endDate === "number";

      if (hasValidCustomDates) {
        setSavedDateRange({
          startDateTime: new Date(startDate),
          endDateTime: new Date(endDate),
        });
      } else {
        setSavedDateRange(null);
      }

      const metricsConfiguration =
        normalizedKey === DATE_RANGE.custom && hasValidCustomDates
          ? {
              filterCampaingsByDates: DATE_RANGE.custom,
              customDates: { startDate, endDate },
            }
          : {
              filterCampaingsByDates: normalizedKey,
              customDates: null,
            };

      dispatch(
        startSetHomeMetricsConfiguration({
          metricsConfiguration,
        })
      );
    },
    [dispatch]
  );

  // Load campaigns on mount
  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [userID, isLoadingConfiguration]);

  // Reset and reload campaigns when sort configuration changes
  useEffect(() => {
    // Skip on first render (already loaded in userID effect)
    if (campaigns === null) return;

    getInitialData();
    // eslint-disable-next-line
  }, [sortConfig.key, sortConfig.direction]);

  // Sync with Redux adsCampaigns
  useEffect(
    () => {
      const data = {};
      const campaignsActive = [];
      const overridesToClear = [];

      Object.keys(adsCampaigns).forEach((campaignID) => {
        const campaign = adsCampaigns[campaignID] || {};
        const keys = Object.keys(campaign);

        // Only show campaigns with complete information
        if (keys.length <= 3) return;

        const statusCampaign = getStatusAdsCampaign({
          states: campaign.states,
          target: "general",
        });

        const overrideStatus = statusOverrides[campaignID];
        data[campaignID] = overrideStatus
          ? {
              ...campaign,
              states: {
                ...campaign.states,
                general: overrideStatus,
              },
            }
          : campaign;

        if (overrideStatus && overrideStatus === statusCampaign) {
          overridesToClear.push(campaignID);
        }

        if (
          statusCampaign !== ADS_CAMPAIGN_STATUS_DRAFT &&
          !campaignsSynced.includes(campaignID)
        )
          campaignsActive.push(campaignID);
      });

      if (campaignsActive.length > 0) {
        setCampaignsSynced((prev) => [...prev, ...campaignsActive]);
        setSyncingCampaignIDs((prev) => [
          ...new Set([...prev, ...campaignsActive]),
        ]);
        onUpdateAdsCampaignInformationFromPlatforms(campaignsActive).finally(
          () => {
            setSyncingCampaignIDs((prev) =>
              prev.filter((id) => !campaignsActive.includes(id))
            );
          }
        );
      }

      if (overridesToClear.length > 0) {
        dispatch(removeAdsCampaignStatusOverrides(overridesToClear));
      }

      setCampaigns(data);
    },
    // eslint-disable-next-line
    [adsCampaigns]
  );

  // Handle successfully published campaigns
  useEffect(() => {
    const successCampaigns = publishingCampaigns
      .filter((campaign) => campaign.status === ALERTS_ASYNC_STATUS.SUCCESS)
      .map((campaign) => campaign.campaignID);

    if (successCampaigns.length > 0) {
      successCampaigns.forEach((campaignID) => {
        dispatch(startGetAdsCampaign(campaignID, true));
      });
    }
    // eslint-disable-next-line
  }, [publishingCampaigns]);

  return {
    campaigns,
    onChangeCampaigns: setCampaigns,
    isLoadingConfiguration,
    isLoadingCampaigns: isLoading,
    sortConfig,
    onTableSort,
    lastCampaignID,
    loadedLastCampaign,
    onGetNextCampaigns,
    isSyncing,
    syncingCampaignIDs,
    datePresetKey,
    onChangeDatePresetKey,
    savedDateRange,
  };
};

export default useAdsCampaignsList;
